// src/App.js
import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { api } from './api';

/* ─── pages ─── */
import ProjectOverview   from './Pages/ProjectOverview';
import ProjectDetails    from './Pages/ProjectDetails';
import ReleaseOverview   from './Pages/ReleaseOverview';
import ReleaseDetails    from './Pages/ReleaseDetails';
import TodoTemplates     from './Pages/TodoTemplates';
import TemplateDetails   from './Pages/TemplateDetails';
import Artists           from './Pages/Artists';
import Invoices          from './Pages/Invoices';
import InvoiceDetails    from './Pages/InvoiceDetails';
import MarketingTasks    from './Pages/MarketingTasks';
import LoginPage         from './Pages/LoginPage';
import OperationsTasks   from './Pages/OperationsTasks';
import CalendarView      from './Pages/CalendarView';
import YouTubeView       from './Pages/YouTubeView';
import AssetCollection   from './Pages/AssetCollection';
import ContractView      from './Pages/ContractView';
import ContractTracking  from './Pages/ContractTracking';
import Writers           from './Pages/Writers';

/* ─── components ─── */
import Sidebar      from './Components/Sidebar';
import PrivateRoute from './Components/PrivateRoute';
import AudioPlayer  from './Components/AudioPlayer';

/* ─── contexts ─── */
import { AudioProvider, useAudio } from './contexts/AudioContext';

import './App.css';

// Component wrapper to check for public view
const MaybePublicRoute = ({ children }) => {
  const isPublic = window.location.search.includes('public=true');
  if (isPublic) {
    return children;
  }
  return <PrivateRoute>{children}</PrivateRoute>;
};

export default function App() {
  /* ─── auth ─── */
  const [isLoggedIn, setIsLoggedIn] = useState(!!localStorage.getItem('token'));
  const handleLoginSuccess = () => setIsLoggedIn(true);

  /* ─── route gates for heavy lists ─── */
  const pathname = typeof window !== 'undefined' ? window.location.pathname : '';

  const needsReleases =
    /^\/track-overview(\/|$)/.test(pathname) ||
    /^\/project\/[^/]+/.test(pathname) ||
    /^\/release\/[^/]+/.test(pathname);

  const needsInvoices =
    /^\/invoices(\/|$)/.test(pathname) ||
    /^\/invoice\/[^/]+/.test(pathname);

  /* ─── projects ─── */
  const [projects, setProjects]         = useState([]);
  const [loadingProjects, setLP]        = useState(false);
  const [projectsError, setProjectsErr] = useState('');

useEffect(() => {
  if (!isLoggedIn) return;
  (async () => {
    try {
      setLP(true);
      setProjectsErr('');
      const token = localStorage.getItem('token');

      const res = await api('/api/projects/paged?limit=50&page=1&fields=title,releaseDate,artists,projectType,isArchived', {
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`Failed to fetch projects. Status: ${res.status}`);

      const data = await res.json();
      setProjects(Array.isArray(data?.items) ? data.items : []);
    } catch (err) {
      setProjectsErr(err.message);
    } finally {
      setLP(false);
    }
  })();
}, [isLoggedIn]);

  /* ─── releases ─── */
  const [releases, setReleases]         = useState([]);
  const [loadingReleases, setLR]        = useState(false);
  const [releasesError, setReleasesErr] = useState('');

  useEffect(() => {
  if (!isLoggedIn) return;
  (async () => {
    try {
      setLR(true);
      setReleasesErr('');
      const token = localStorage.getItem('token');

      const res = await api('/api/releases/paged?limit=50&page=1', {
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`Failed to fetch releases. Status: ${res.status}`);

      const data = await res.json();
      setReleases(Array.isArray(data?.items) ? data.items : []);
    } catch (err) {
      setReleasesErr(err.message);
    } finally {
      setLR(false);
    }
  })();
}, [isLoggedIn]);

  /* ─── invoices ─── */
  const [invoices, setInvoices]         = useState([]);
  const [loadingInvoices, setLI]        = useState(false);
  const [invoicesError, setInvoicesErr] = useState('');

  useEffect(() => {
    if (!isLoggedIn || !needsInvoices) return;
    (async () => {
      try {
        setLI(true);
        setInvoicesErr('');
        const token = localStorage.getItem('token');
        const res = await api('/api/invoices', {
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        });
        if (!res.ok) throw new Error(`Failed to fetch invoices. Status: ${res.status}`);
        setInvoices(await res.json());
      } catch (err) {
        setInvoicesErr(err.message);
      } finally {
        setLI(false);
      }
    })();
  }, [isLoggedIn, needsInvoices]);

  const handleInvoiceStatusChange = async (invoiceId, newStatus) => {
    try {
      const token = localStorage.getItem('token');
      const res = await api(`/api/invoices/${invoiceId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ paymentStatus: newStatus }),
      });
      if (!res.ok) throw new Error(`Failed to update invoice. Status: ${res.status}`);
      const updated = await res.json();
      setInvoices(prev => prev.map(inv => (inv._id === invoiceId ? updated : inv)));
    } catch (err) {
      console.error(err);
      alert(err.message);
    }
  };

  /* ─── todo templates ─── */
  const [templates, setTemplates] = useState([]);

  useEffect(() => {
    if (!isLoggedIn) return;
    (async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await api('/api/todotemplates', {
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        });
        if (!res.ok) throw new Error(`Failed to fetch templates. Status: ${res.status}`);
        setTemplates(await res.json());
      } catch (err) {
        console.error(err);
      }
    })();
  }, [isLoggedIn]);

  /* ─── render ─── */
  return (
    <AudioProvider>
      <Router>
        <AppContent
          isLoggedIn={isLoggedIn}
          handleLoginSuccess={handleLoginSuccess}
          projects={projects}
          setProjects={setProjects}
          releases={releases}
          invoices={invoices}
          setInvoices={setInvoices}
          templates={templates}
          setTemplates={setTemplates}
          handleInvoiceStatusChange={handleInvoiceStatusChange}
          loadingProjects={loadingProjects}
          projectsError={projectsError}
          needsReleases={needsReleases}
          loadingReleases={loadingReleases}
          releasesError={releasesError}
          loadingInvoices={loadingInvoices}
          invoicesError={invoicesError}
        />
      </Router>
    </AudioProvider>
  );
}

function AppContent({
  isLoggedIn,
  handleLoginSuccess,
  projects,
  setProjects,
  releases,
  invoices,
  setInvoices,
  templates,
  setTemplates,
  handleInvoiceStatusChange,
  loadingProjects,
  projectsError,
  needsReleases,
  loadingReleases,
  releasesError,
  loadingInvoices,
  invoicesError,
}) {
  const { currentTrack, closePlayer } = useAudio();
  
  // Check if current URL has public=true parameter
  const isPublicView = window.location.search.includes('public=true');

  return (
    <>
      <div className="app-container">
        {!isPublicView && isLoggedIn && <Sidebar />}

        <div 
          className="content-container" 
          style={{ 
            padding: 20,
            paddingBottom: currentTrack ? 110 : 20, // Add space for player
            marginLeft: isPublicView ? 'auto' : '220px',
            marginRight: isPublicView ? 'auto' : '0',
            maxWidth: isPublicView ? '1600px' : 'none',
          }}
        >
          {isLoggedIn && loadingProjects && <p style={{ color: '#fff' }}>Loading projects…</p>}
          {projectsError && <p style={{ color: 'red' }}>Projects Error: {projectsError}</p>}

          {isLoggedIn && needsReleases && loadingReleases && (
            <p style={{ color: '#fff' }}>Loading releases…</p>
          )}
          {needsReleases && releasesError && (
            <p style={{ color: 'red' }}>Releases Error: {releasesError}</p>
          )}

          {isLoggedIn && loadingInvoices && <p style={{ color: '#fff' }}>Loading invoices…</p>}
          {invoicesError && <p style={{ color: 'red' }}>Invoices Error: {invoicesError}</p>}

          <Routes>
            <Route path="/login" element={<LoginPage onLoginSuccess={handleLoginSuccess} />} />

            {/* root now shows PROJECT overview */}
            <Route
              path="/"
              element={
                <PrivateRoute>
                  <ProjectOverview projects={projects} />
                </PrivateRoute>
              }
            />

            {/* Release Overview (primary) */}
            <Route
              path="/release-overview"
              element={
                <MaybePublicRoute>
                  <ReleaseOverview releases={releases} templates={templates} />
                </MaybePublicRoute>
              }
            />
            
            {/* Alias for backward compatibility */}
            <Route
              path="/track-overview"
              element={
                <MaybePublicRoute>
                  <ReleaseOverview releases={releases} templates={templates} />
                </MaybePublicRoute>
              }
            />

            {/* keep project overview alias if desired */}
            <Route
              path="/project-overview"
              element={
                <PrivateRoute>
                  <ProjectOverview projects={projects} />
                </PrivateRoute>
              }
            />

            <Route
              path="/release/:id"
              element={
                <PrivateRoute>
                  <ReleaseDetails templates={templates} />
                </PrivateRoute>
              }
            />

            <Route
              path="/project/:id"
              element={
                <PrivateRoute>
                  <ProjectDetails
                    projects={projects}
                    setProjects={setProjects}
                    releases={releases}
                    invoices={invoices}
                    setInvoices={setInvoices}
                    handleInvoiceStatusChange={handleInvoiceStatusChange}
                  />
                </PrivateRoute>
              }
            />

            <Route
              path="/artists"
              element={
                <PrivateRoute>
                  <Artists />
                </PrivateRoute>
              }
            />

            <Route
              path="/writers"
              element={
                <PrivateRoute>
                  <Writers />
                </PrivateRoute>
              }
            />

            <Route
              path="/todo-templates"
              element={
                <PrivateRoute>
                  <TodoTemplates templates={templates} setTemplates={setTemplates} />
                </PrivateRoute>
              }
            />

            <Route
              path="/todo-template/:id"
              element={
                <PrivateRoute>
                  <TemplateDetails templates={templates} setTemplates={setTemplates} />
                </PrivateRoute>
              }
            />

            <Route
              path="/invoices"
              element={
                <PrivateRoute>
                  <Invoices
                    projects={projects}
                    releases={releases}
                    invoices={invoices}
                    setInvoices={setInvoices}
                    handleInvoiceStatusChange={handleInvoiceStatusChange}
                  />
                </PrivateRoute>
              }
            />

            <Route
              path="/invoice/:id"
              element={
                <PrivateRoute>
                  <InvoiceDetails
                    invoices={invoices}
                    setInvoices={setInvoices}
                    projects={projects}
                    releases={releases}
                  />
                </PrivateRoute>
              }
            />

            <Route
              path="/operations-tasks"
              element={
                <PrivateRoute>
                  <OperationsTasks />
                </PrivateRoute>
              }
            />

            <Route path="/asset-collection/:id" element={<AssetCollection />} />

            <Route
              path="/contract/:id"
              element={
                <PrivateRoute>
                  <ContractView />
                </PrivateRoute>
              }
            />

            <Route
              path="/contract-tracking"
              element={
                <PrivateRoute>
                  <ContractTracking />
                </PrivateRoute>
              }
            />

            <Route path="/calendar" element={<CalendarView />} />

            <Route path="/youtube" element={<PrivateRoute><YouTubeView /></PrivateRoute>} />

            <Route
              path="/marketing-tasks"
              element={
                <PrivateRoute>
                  <MarketingTasks />
                </PrivateRoute>
              }
            />
          </Routes>
        </div>
      </div>

      {/* Global Audio Player */}
      {currentTrack && (
        <AudioPlayer
          audioUrl={currentTrack.audioUrl}
          trackTitle={currentTrack.trackTitle}
          artistName={currentTrack.artistName}
          coverArt={currentTrack.coverArt}
          onClose={closePlayer}
        />
      )}
    </>
  );
}